import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { useQuery, useMutation, useQueryClient } from '@sopranodesign/pulse-hooks';
import {
  PageWrapper,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableActions,
  OrderableTableRow,
  useOrderableTableReducer,
  StretchPanel as Panel,
  EmptyStatePlaceholder,
  useNotification,
  ModalPortal,
  ModalContainer,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Flex,
  Explainer
} from '@sopranodesign/pulse-components';
import { PermissionFilter } from '@sopranodesign/pulse-auth';

import { deleteProvider } from '../api/index';

const McpProviders = () => {
  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState(null);

  const query = useQuery({ queryKey: ['mcp-skill-providers'] });

  const mutation = useMutation({
    mutationFn: deleteProvider,
    onSuccess: () => {
      showSuccess('Provider deleted');
      setDeleting(null);
      queryClient.invalidateQueries('mcp-skill-providers');
    }
  });

  const order = useOrderableTableReducer({ id: 'mcp-providers' });

  const sortedProviders = React.useMemo(() => {
    const data = query.data?.data ?? [];
    if (!order.order || !order.direction) { return data; }
    return [...data].sort((a, b) => {
      let aVal = a[order.order];
      let bVal = b[order.order];
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      } else {
        aVal = String(aVal ?? '').toLowerCase();
        bVal = String(bVal ?? '').toLowerCase();
      }
      if (aVal < bVal) { return order.direction === 'ascending' ? -1 : 1; }
      if (aVal > bVal) { return order.direction === 'ascending' ? 1 : -1; }
      return 0;
    });
  }, [query.data, order.order, order.direction]);

  const handleDelete = provider => () => setDeleting(provider);

  return (
    <PageWrapper
      header="MCP Skill Providers"
      subheader="Manage MCP servers and their skills"
      actions={
        query.isSuccess &&
        !query.showNoResultsMessage && (
          <PermissionFilter can="create mcp skill providers">
            <Button
              as={Link}
              to="/mcp/providers/create"
              variant="primary"
              aria-label="Create provider">
              + New Provider
            </Button>
          </PermissionFilter>
        )
      }>
      {deleting && (
        <ModalPortal>
          <ModalContainer>
            <ModalContent>
              <ModalHeader>Delete {deleting.name}?</ModalHeader>
              <ModalBody>
                <Explainer type="warning">
                  Deleting this provider will permanently remove it and all of its skills.
                </Explainer>
              </ModalBody>
              <ModalFooter>
                <Flex right xSpace>
                  <Button variant="secondary" onClick={() => setDeleting(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="secondary"
                    colour="danger"
                    icon="trash"
                    loading={mutation.isPending}
                    onClick={() => mutation.mutate(deleting.id)}>
                    Delete
                  </Button>
                </Flex>
              </ModalFooter>
            </ModalContent>
          </ModalContainer>
        </ModalPortal>
      )}
      <Panel>
        {query.showNoResultsMessage && (
          <EmptyStatePlaceholder
            type="integrations"
            heading="Add your first MCP provider"
            text="Connect to any MCP-compatible server to use its tools in conversations."
            actions={
              <PermissionFilter can="create mcp skill providers">
                <Button
                  variant="primary"
                  as={Link}
                  to="/mcp/providers/create"
                  aria-label="Create provider">
                  + New Provider
                </Button>
              </PermissionFilter>
            }
          />
        )}

        {query.showTable && (
          <Table loading={query.isLoading} loadingColumns={4}>
            <TableHead>
              <OrderableTableRow
                rows={[
                  { label: 'Name', sort: 'name' },
                  { label: 'Driver', sort: 'driver' },
                  { label: 'Status', sort: 'enabled' },
                  null
                ]}
                {...order.props}
              />
            </TableHead>
            <TableBody>
              {sortedProviders.map(provider => (
                <TableRow key={provider.id}>
                  <TableCell>{provider.name}</TableCell>
                  <TableCell>{provider.driver ?? '—'}</TableCell>
                  <TableCell>
                    {provider.enabled ? 'Enabled' : 'Disabled'}
                  </TableCell>
                  <TableActions>
                      <Button
                        as={Link}
                        to={`/mcp/providers/${provider.id}`}
                        pad="true"
                        icon="pencilAlt"
                        aria-label="Edit provider"
                      > Edit </Button>
                      <PermissionFilter can="delete mcp skill providers">
                        <Button
                          icon="trash"
                          colour="danger"
                          pad="true"
                          aria-label="Delete provider"
                          onClick={handleDelete(provider)}
                        > Delete </Button>
                      </PermissionFilter>
                    </TableActions>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
    </PageWrapper>
  );
};

export default McpProviders;
